import React from 'react';
import { useCookies } from 'react-cookie';
import { useLocalStorage } from 'react-use';
import Tray from '@grnhse/seedling/lib/azalea/components/trays/tray';
import TrayContainer from '@grnhse/seedling/lib/azalea/components/trays/tray_container';
import RecentlyViewedItem from './recently_viewed_item';
import {
  Tab,
  TabbedCard,
} from '@grnhse/seedling/lib/azalea/components/tabbed_card_2';
import styled from 'styled-components';
import {
  colors,
  fontSize,
  fontWeight,
} from '@grnhse/seedling/lib/azalea/constants';
import { trackEvent } from '@grnhse/datacoral_tracker';
import t from 'shared/utils/translation';

// Either 'greenhouse.io' on production or 'greenhouse.dev' on dev environments
const GREENHOUSE_DOMAIN = window.location.hostname
  .split('.')
  .slice(-2)
  .join('.');

type Candidate = {
  name: string;
  job: string;
  url: string;
};

type Job = {
  name: string;
  location: string;
  url: string;
};
function RecentlyViewedComponent() {
  const STORAGE_OPEN = 'rv_open';
  const COOKIE_TYPE = 'rv_type';
  const COOKIE_CONTENT = 'rv_content';

  const TABS = ['candidates', 'jobs'];

  const [cookies, setCookie] = useCookies([COOKIE_TYPE, COOKIE_CONTENT]);
  const [expanded, setExpanded] = useLocalStorage(STORAGE_OPEN, false);
  const selectedIndex = TABS.indexOf(cookies[COOKIE_TYPE] || 'candidates');
  const content = cookies[COOKIE_CONTENT] || {};

  if (content.candidates) {
    content.candidates = content.candidates.filter((candidate: Candidate) => {
      return candidate.url.startsWith('/');
    });
  }

  if (content.jobs) {
    content.jobs = content.jobs.filter((job: Job) => {
      return job.url.startsWith('/');
    });
  }

  const expandedChanged = () => {
    setExpanded(!expanded);
    expanded
      ? trackEvent('recently-viewed:close', {})
      : trackEvent('recently-viewed:open', {});
  };

  const tabChanged = (data: any) => {
    setCookie(COOKIE_TYPE, TABS[data.index], {
      domain: GREENHOUSE_DOMAIN,
      path: '/',
    });
    trackEvent('recently-viewed:change-tab', { tab_name: TABS[data.index] });
  };

  return (
    <Outer
      data-provides="recently-viewed-tray"
      data-provides-of={expanded ? 'expanded' : 'minimized'}
    >
      <TrayContainer zIndex={998} position="fixed">
        <Tray
          title={t('recently_viewed.header')}
          expanded={!!expanded}
          onExpandChange={expandedChanged}
          width={230}
          maxHeight={320}
        >
          <RecentlyTabbedCard
            onTabSelect={tabChanged}
            selectedIndex={selectedIndex}
          >
            <RecentlyTypeTab
              title={t('recently_viewed.candidates.header')}
              data-provides="recently_viewed_candidates_header"
            >
              <List data-provides="recently_viewed_candidates">
                {content.candidates && content.candidates.length > 0 ? (
                  // @ts-expect-error - TS7006 - Parameter 'candidate' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
                  content.candidates.map((candidate, index) => {
                    return (
                      <RecentlyViewedItem
                        key={index}
                        url={candidate.url}
                        title={candidate.name}
                        subtitle={candidate.job}
                      />
                    );
                  })
                ) : (
                  <EmptyRow>{t('recently_viewed.candidates.empty')}</EmptyRow>
                )}
              </List>
            </RecentlyTypeTab>
            <RecentlyTypeTab
              title={t('recently_viewed.jobs.header')}
              data-provides="recently_viewed_jobs_header"
            >
              <List data-provides="recently_viewed_jobs">
                {content.jobs && content.jobs.length > 0 ? (
                  // @ts-expect-error - TS7006 - Parameter 'job' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
                  content.jobs.map((job, index) => {
                    return (
                      <RecentlyViewedItem
                        key={index}
                        url={job.url}
                        title={job.name}
                        subtitle={job.location}
                      />
                    );
                  })
                ) : (
                  <EmptyRow>{t('recently_viewed.jobs.empty')}</EmptyRow>
                )}
              </List>
            </RecentlyTypeTab>
          </RecentlyTabbedCard>
        </Tray>
      </TrayContainer>
    </Outer>
  );
}

export default RecentlyViewedComponent;

const List = styled.ul`
  height: 203px;
  overflow-y: auto;
  list-style: none;
  border: 0;
  margin: 0;
  padding: 0;
`;

const RecentlyTabbedCard = styled(TabbedCard)`
  margin: 0;

  [role='tabpanel'] div {
    border: 0;
  }

  header ul {
    justify-content: space-evenly;
    height: 44px;

    li {
      margin: 0;
      padding: 0;
      width: 50%;
    }
  }
`;

const EmptyRow = styled.li`
  border: 0;
  margin: 0;
  padding: 20px 7px;
  min-height: 65px;
  font-size: ${fontSize.large};
  line-height: 1.8;
  color: ${colors.mediumGrey};
  text-align: center;
  font-weight: ${fontWeight.normal};
`;

const RecentlyTypeTab = styled(Tab)`
  padding: 0;
`;

const Outer = styled.div`
  font-family: 'Untitled Sans', sans-serif;

  & .tray-content {
    border: 0;
    overflow-y: hidden;
  }
`;
